import React, { useState, useCallback, useEffect } from 'react';
import { useAuth, SignInButton } from '@clerk/clerk-react';
import { useStrategies, deleteStrategy, runStrategy, useStrategyAlerts, isSwrAuthReady } from '../../swr-config';
import { StrategyCard } from './StrategyCard';
import { StrategyCreatorModal } from './StrategyCreatorModal';
import { StrategyResultsView } from './StrategyResultsView';
import { StrategyAlertBanner } from './StrategyAlertBanner';
import { useFeatureTracking, FeatureNames } from '../../hooks/useActivityTracking';
import type { BetStrategy, StrategyRunResponse } from '../../types';

type View = 'list' | 'results';

export function StrategiesPage() {
  // Track feature usage
  useFeatureTracking(FeatureNames.STRATEGIES);

  const { isSignedIn, isLoaded: authLoaded } = useAuth();

  // Track when SWR auth token getter is configured
  // This prevents 401 errors from race conditions on page refresh
  const [swrAuthReady, setSwrAuthReady] = useState(isSwrAuthReady());

  useEffect(() => {
    if (swrAuthReady) return; // Already ready

    // Poll for auth readiness (token getter being set)
    const checkAuth = () => {
      if (isSwrAuthReady()) {
        setSwrAuthReady(true);
      }
    };

    // Check immediately and then poll
    checkAuth();
    const interval = setInterval(checkAuth, 50);

    // Stop polling after 5 seconds to prevent infinite loops
    const timeout = setTimeout(() => clearInterval(interval), 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [swrAuthReady]);

  // Only fetch strategies when user is authenticated AND SWR auth token getter is configured
  // This prevents 401 errors during the auth loading phase and race conditions on refresh
  const shouldFetch = authLoaded && isSignedIn && swrAuthReady;
  const { data, error, isLoading, mutate } = useStrategies(shouldFetch);
  const { data: alertsData, mutate: mutateAlerts } = useStrategyAlerts(shouldFetch);

  const [view, setView] = useState<View>('list');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState<BetStrategy | null>(null);
  const [runResults, setRunResults] = useState<StrategyRunResponse | null>(null);
  const [runningStrategyId, setRunningStrategyId] = useState<number | null>(null);

  // All useCallback hooks must be declared before any conditional returns
  const handleCreate = useCallback(() => {
    setEditingStrategy(null);
    setShowCreateModal(true);
  }, []);

  const handleEdit = useCallback((strategy: BetStrategy) => {
    setEditingStrategy(strategy);
    setShowCreateModal(true);
  }, []);

  const handleDelete = useCallback(async (strategyId: number) => {
    if (!confirm('Are you sure you want to delete this strategy?')) {
      return;
    }
    try {
      await deleteStrategy(strategyId);
      mutate();
    } catch (err) {
      console.error('Failed to delete strategy:', err);
      alert(err instanceof Error ? err.message : 'Failed to delete strategy');
    }
  }, [mutate]);

  const handleRun = useCallback(async (strategy: BetStrategy) => {
    setRunningStrategyId(strategy.id);
    try {
      const results = await runStrategy(strategy.id, { per_page: 20 });
      setRunResults(results);
      setView('results');
      mutate(); // Refresh to update last_run_at
    } catch (err) {
      console.error('Failed to run strategy:', err);
      alert(err instanceof Error ? err.message : 'Failed to run strategy');
    } finally {
      setRunningStrategyId(null);
    }
  }, [mutate]);

  const handleLoadMore = useCallback(async () => {
    if (!runResults || !runResults.has_more) return;
    try {
      const nextPage = await runStrategy(runResults.strategy_id, {
        page: runResults.page + 1,
        per_page: runResults.per_page,
      });
      setRunResults({
        ...nextPage,
        matches: [...runResults.matches, ...nextPage.matches],
      });
    } catch (err) {
      console.error('Failed to load more:', err);
    }
  }, [runResults]);

  const handleBackToList = useCallback(() => {
    setView('list');
    setRunResults(null);
  }, []);

  const handleModalClose = useCallback(() => {
    setShowCreateModal(false);
    setEditingStrategy(null);
  }, []);

  const handleModalSave = useCallback(() => {
    mutate();
    handleModalClose();
  }, [mutate, handleModalClose]);

  const handleDismissAlerts = useCallback(() => {
    mutateAlerts();
  }, [mutateAlerts]);

  // Now we can have conditional returns after all hooks are declared

  // Show loading state while auth is being checked or SWR auth is being configured
  if (!authLoaded || (isSignedIn && !swrAuthReady)) {
    return (
      <div className="strategies-page">
        <div className="strategies-loading">
          <div className="loading-spinner" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!isSignedIn) {
    return (
      <div className="strategies-page">
        <div className="strategies-empty">
          <div className="strategies-empty-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4z" />
              <path d="M6 21v-2c0-2.21 1.79-4 4-4h4c2.21 0 4 1.79 4 4v2" />
            </svg>
          </div>
          <h3>Sign in required</h3>
          <p>Create and manage your betting strategies by signing in to your account.</p>
          <SignInButton mode="modal">
            <button className="strategies-create-btn">
              Sign In
            </button>
          </SignInButton>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="strategies-page">
        <div className="strategies-loading">
          <div className="loading-spinner" />
          <p>Loading strategies...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const isAuthError = errorMessage.includes('401') || errorMessage.includes('Authentication');

    return (
      <div className="strategies-page">
        <div className="strategies-error">
          <p>{isAuthError ? 'Please sign in to view your strategies' : 'Failed to load strategies'}</p>
          {isAuthError ? (
            <SignInButton mode="modal">
              <button className="strategies-create-btn">Sign In</button>
            </SignInButton>
          ) : (
            <button onClick={() => mutate()}>Retry</button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="strategies-page">
      {/* Alert Banner */}
      {alertsData && alertsData.total_new_matches > 0 && (
        <StrategyAlertBanner
          alerts={alertsData}
          onDismiss={handleDismissAlerts}
          onViewStrategy={(id) => {
            const strategy = data.strategies.find(s => s.id === id);
            if (strategy) handleRun(strategy);
          }}
        />
      )}

      {view === 'list' && (
        <>
          {/* Header */}
          <div className="strategies-header">
            <div className="strategies-header-left">
              <h1>Bet Strategies</h1>
              <span className="strategies-count">
                {data.total} / 20 strategies
              </span>
            </div>
            <button
              className="strategies-create-btn"
              onClick={handleCreate}
              disabled={data.total >= 20}
              title={data.total >= 20 ? 'Maximum 20 strategies reached' : 'Create new strategy'}
            >
              + New Strategy
            </button>
          </div>

          {/* Strategy List */}
          {data.strategies.length === 0 ? (
            <div className="strategies-empty">
              <div className="strategies-empty-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M3 4h18M3 12h18M3 20h18" />
                  <circle cx="6" cy="4" r="1" />
                  <circle cx="6" cy="12" r="1" />
                  <circle cx="6" cy="20" r="1" />
                </svg>
              </div>
              <h3>No strategies yet</h3>
              <p>Create automated betting strategies to find matching fixtures based on your criteria.</p>
              <button className="strategies-create-btn" onClick={handleCreate}>
                Create Your First Strategy
              </button>
            </div>
          ) : (
            <div className="strategies-list">
              {data.strategies.map(strategy => (
                <StrategyCard
                  key={strategy.id}
                  strategy={strategy}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onRun={handleRun}
                  isRunning={runningStrategyId === strategy.id}
                />
              ))}
            </div>
          )}
        </>
      )}

      {view === 'results' && runResults && (
        <StrategyResultsView
          results={runResults}
          onBack={handleBackToList}
          onLoadMore={handleLoadMore}
        />
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <StrategyCreatorModal
          strategy={editingStrategy}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
}

export default StrategiesPage;
